import React from 'react';
import { cn } from '../../lib/utils';
import DefaultChatContainer from './default-components/DefaultChatContainer';

interface ChatContainerProps {
  isWidgetChat: boolean;
  generalBackground?: string;
  borderColor?: string;
  agentId?: string;
  customChatContainer?: React.ComponentType<any>;
  scrollToBottomButton?: React.ReactNode;
  shareModal?: React.ReactNode;
  messagesArea?: React.ReactNode;
  inputArea?: React.ReactNode;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  isWidgetChat,
  generalBackground,
  borderColor,
  agentId,
  customChatContainer: CustomChatContainer = DefaultChatContainer,
  scrollToBottomButton,
  shareModal,
  messagesArea,
  inputArea,
}) => {
  const widgetContainerClasses = cn(
    'bitte-relative bitte-w-full bitte-h-full bitte-flex bitte-flex-col bitte-gap-4',
    'bitte-bg-mb-gray-150',
  );

  const chatMainClasses = cn(
    'bitte-chat-main bitte-text-start bitte-flex-1 bitte-relative',
    'bitte-min-h-[360px] bitte-w-full bitte-overflow-hidden bitte-pl-6',
  );

  const scrollableAreaClasses = cn(
    'bitte-absolute bitte-inset-0 bitte-flex bitte-h-full bitte-w-full',
    'bitte-justify-center bitte-overflow-y-auto bitte-overflow-x-hidden',
    'bitte-p-4 bitte-bg-mb-gray-250',
  );

  const messagesContainerClasses = cn(
    'bitte-mx-4 bitte-flex bitte-w-full bitte-flex-col bitte-gap-6',
    !!agentId ? 'bitte-h-[calc(100%-240px)]' : 'bitte-h-[calc(100%-208px)]',
  );

  if (isWidgetChat && messagesArea && inputArea) {
    return (
      <div className={widgetContainerClasses}>
        <div className={chatMainClasses}>
          {scrollToBottomButton}
          <div className={scrollableAreaClasses}>
            <div className={messagesContainerClasses}>{messagesArea}</div>
          </div>
        </div>
        {inputArea}
      </div>
    );
  }

  // Original logic for standard chat or fallback
  const chatContainerClass = isWidgetChat
    ? widgetContainerClasses
    : cn(
        'bitte-relative bitte-w-full bitte-custom-scrollbar bitte-h-full',
        'bitte-flex bitte-flex-col bitte-gap-4',
      );

  const standardScrollableClasses = cn(
    'bitte-absolute bitte-inset-0 bitte-flex bitte-h-full bitte-w-full',
    'bitte-justify-center bitte-overflow-y-auto bitte-overflow-x-hidden bitte-p-4',
  );

  const standardMessagesClasses = cn(
    'bitte-mx-auto bitte-flex bitte-w-full bitte-flex-col bitte-gap-6',
    'md:bitte-mx-24 2xl:bitte-mx-40',
    !!agentId ? 'bitte-h-[calc(100%-240px)]' : 'bitte-h-[calc(100%-208px)]',
  );

  const containerContent = isWidgetChat ? (
    <div className={chatMainClasses}>
      {scrollToBottomButton}
      <div className={scrollableAreaClasses}>
        <div className={messagesContainerClasses}>{messagesArea}</div>
      </div>
    </div>
  ) : (
    <CustomChatContainer
      style={{
        backgroundColor: generalBackground,
        borderColor: borderColor,
      }}
    >
      {scrollToBottomButton}
      {shareModal}
      <div className={standardScrollableClasses}>
        <div className={standardMessagesClasses}>{messagesArea}</div>
      </div>
    </CustomChatContainer>
  );

  return (
    <div className={chatContainerClass}>
      {containerContent} {inputArea}
    </div>
  );
};
